import React, { useState } from "react";
import { toast } from "sonner";
import { Globe, Loader2, Sparkles } from "lucide-react";
import { Btn, inputCls } from "./Kit";
import { api, errMessage } from "../../lib/api";

export const WebsiteImport = ({ initialUrl = "", onApplied }) => {
  const [url, setUrl] = useState(initialUrl);
  const [found, setFound] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const scan = async () => {
    if (!url.trim()) {
      toast.error("Enter your website address first.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/engine/import-website", { url });
      setFound(data);
      toast.success("We read your website. Check what we found before filling it in.");
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    setApplying(true);
    try {
      const { data } = await api.post("/engine/apply-website-import", { ...found, url });
      toast.success("Your Brand Profile has been filled in — please review every step.");
      onApplied?.(data.profile);
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setApplying(false);
    }
  };

  const list = (v) => (Array.isArray(v) ? v.join(", ") : v || "");

  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="website-import-panel">
      <p className="font-display text-base font-semibold text-slate-900">Import from your website</p>
      <p className="mt-1 text-sm text-slate-600">
        We read your public pages and fill in your name, services, hours, colours, logo and social links. You review
        everything after.
      </p>
      <div className="mt-4 flex flex-col gap-3 sm:flex-row">
        <input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.nl"
          data-testid="website-import-url-input"
          className={inputCls}
        />
        <Btn onClick={scan} disabled={loading} testId="website-import-scan-button" className="shrink-0">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Globe className="h-4 w-4" />}
          {loading ? "Reading your site…" : "Scan my website"}
        </Btn>
      </div>

      {found && (
        <div className="mt-5 space-y-3" data-testid="website-import-result">
          {[
            ["Business name", found.business_name],
            ["Industry", found.industry],
            ["Location", found.address || found.location],
            ["Opening hours", found.opening_hours],
            ["Services", list(found.services)],
            ["Contact", [found.phone, found.email].filter(Boolean).join(" · ")],
            ["Socials", Object.keys(found.socials || {}).join(", ")],
            ["Pages read", (found.pages_read || []).length],
          ].map(([label, value]) => (
            <div key={label} className="flex flex-wrap gap-2 text-sm">
              <span className="w-32 shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</span>
              <span className="min-w-0 flex-1 text-slate-800">{value || "—"}</span>
            </div>
          ))}

          {(found.brand_colors || []).length > 0 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="website-import-colors">
              {found.brand_colors.map((c) => (
                <span key={c} className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-xs">
                  <span className="h-4 w-4 rounded" style={{ background: c }} aria-hidden="true" />
                  {c}
                </span>
              ))}
            </div>
          )}

          {found.logo_url && (
            <img
              src={found.logo_url}
              alt="Logo found on your website"
              data-testid="website-import-logo"
              className="h-14 w-auto max-w-[200px] rounded-lg border border-slate-200 bg-white object-contain p-1"
            />
          )}

          {(found.uncertain || []).length > 0 && (
            <div className="rounded-xl bg-amber-50 p-3 text-sm text-amber-900" data-testid="website-import-verify">
              <p className="font-semibold">Please double-check:</p>
              <ul className="mt-1 list-disc pl-5">
                {found.uncertain.map((u) => (<li key={u}>{u}</li>))}
              </ul>
            </div>
          )}

          <Btn onClick={apply} disabled={applying} testId="website-import-apply-button">
            {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {applying ? "Filling in your profile…" : "Fill in my Brand Profile"}
          </Btn>
        </div>
      )}
    </div>
  );
};

export default WebsiteImport;
